import { withDependencies } from '@wix/thunderbolt-ioc'
import type {
	BIReporter,
	CommonConfig,
	BrowserWindow,
	ViewerModel,
	PlatformEnvDataProvider,
	SdkHandlersProvider,
} from '@wix/thunderbolt-symbols'
import { BISymbol, BrowserWindowSymbol, ViewerModelSym } from '@wix/thunderbolt-symbols'
import { isSSR, getCommonConfigHeader } from '@wix/thunderbolt-commons'
import type { ICommonConfig, ICommonConfigState, ICommonConfigWixCodeSdkHandlers } from './types'

const ssrGlobal = globalThis as { commonConfig?: CommonConfig }

export const CommonConfigImpl = withDependencies(
	[ViewerModelSym, BISymbol, BrowserWindowSymbol],
	(
		viewerModel: ViewerModel,
		biReporter: BIReporter,
		window: BrowserWindow
	): ICommonConfig & PlatformEnvDataProvider & SdkHandlersProvider<ICommonConfigWixCodeSdkHandlers> => {
		const state: ICommonConfigState = {
			commonConfig: viewerModel.commonConfig,
			subscribers: new Set(),
		}

		const getCommonConfig = () => state.commonConfig
		const registerToCommonConfigChange: ICommonConfig['registerToCommonConfigChange'] = (subscriber) => {
			state.subscribers.add(subscriber)
			return () => state.subscribers.delete(subscriber)
		}

		return {
			getCommonConfig,
			getCommonConfigForUrl: () => getCommonConfigHeader(state.commonConfig),
			updateCommonConfig: (config: Partial<CommonConfig>) => {
				if (config.hasOwnProperty('bsi')) {
					biReporter.setDynamicSessionData({ bsi: config.bsi })
				}
				state.commonConfig = { ...state.commonConfig, ...config }
				state.subscribers.forEach((subscriber) => subscriber(state.commonConfig))

				if (!isSSR(window)) {
					window!.commonConfig = {
						...window!.commonConfig,
						...state.commonConfig,
					}
				} else if (ssrGlobal.commonConfig) {
					// Sync only, never create: the isolated renderer sets this global per request (`defineRequestGlobals` in
					// thunderbolt-app isolated-renderer/global-env.ts). It is absent on the legacy renderer, whose global is
					// shared by parallel requests, so writing it here unconditionally would leak one request's config into another.
					ssrGlobal.commonConfig = {
						...ssrGlobal.commonConfig,
						...state.commonConfig,
					}
				}
			},
			registerToCommonConfigChange,
			platformEnvData() {
				return { commonConfig: getCommonConfig() }
			},
			getSdkHandlers: () => ({ registerToCommonConfigChange }),
		}
	}
)
